import React, { useState } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Container, Box, Pagination, Alert, CircularProgress } from '@mui/material';
import { useOrderSummary } from '../hooks/useOrderSummaries';
import { OrderSummary } from '../../types/OrderSummary';
import { formatDate } from '../../utils/csvUtils';
import Papa from 'papaparse';
import { supabase } from '../../configs/supabase';
import EditMemoModal from '../../components/editMemoModal';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

export default function OrderCsv() {
  const { orderSummaries, newOrders, fetchOrderSummaries, page, totalPages, setPage } = useOrderSummary();
  const [selectedSummary, setSelectedSummary] = useState<OrderSummary | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCSVExport = async () => {
    if (newOrders.length === 0) {
      setExportMessage("新しい注文データがありません");
      return;
    }

    setLoading(true);
    const filteredOrders = newOrders.map(order => ({
      受注番号: order.受注番号,
      顧客コード: order.顧客コード,
      顧客名: order.顧客名,
      注文日: order.注文日.replace('T', ' '),
      納品希望日: order.納品希望日,
      配送先コード: order.配送先コード,
      配送先名: order.配送先名,
      配送先住所: order.配送先住所,
      "コメント（備考）": order["コメント（備考）"].replace(/\n/g, ""),
      明細番号: order.明細番号,
      商品コード: order.商品コード,
      商品名: order.商品名,
      数量: order.数量,
      単位: order.単位,
      税区分コード: order.税区分コード,
      税抜単価: order.税抜単価,
      税抜合計: order.税抜合計,
      税込合計: order.税込合計
    }));
    
    // 中野さんのみ最後に空行を追加する(マクロ変換の取り込み対応のため)
    if (process.env.REACT_APP_ENVIRONMENT==='NAKANO') {
      filteredOrders.push({
        受注番号: NaN,
        顧客コード: NaN,
        顧客名: '',
        注文日: '',
        納品希望日: '',
        配送先コード: NaN,
        配送先名: '',
        配送先住所: '',
        "コメント（備考）": '',
        明細番号: NaN,
        商品コード: NaN,
        商品名: '',
        数量: NaN,
        単位: '',
        税区分コード: NaN,
        税抜単価: NaN,
        税抜合計: NaN,
        税込合計: NaN
      });
    }
    
    const csv = Papa.unparse(filteredOrders);
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const timestamp = dayjs().format('YYYYMMDDHHmmss');
    const csv_file_name = `0004_order_${timestamp}.csv`
    link.setAttribute('download', csv_file_name);
    link.click();

    const startOrder = newOrders[0];
    const lastOrder = newOrders[newOrders.length - 1];
    const newSummary = {
      start_order_id: startOrder.id,
      last_order_id: lastOrder.id,
      memo: '',
      download_date: dayjs().locale('ja').format(),
      download_status: true,
      csv_file_name: csv_file_name
    };

    const { error } = await supabase
      .from('order_summary')
      .insert([newSummary]);

    if (error) {
      setExportMessage("order_summaryへのレコード追加失敗: " + error.message);
    } else {

      // SupabaseのストレージにCSVファイルをアップロード
      const { error: uploadError } = await supabase.storage
        .from('order-csv')
        .upload(csv_file_name, blob, { upsert: true });

      if (uploadError) {
        setExportMessage("CSVのアップロード失敗: " + uploadError.message);
      } else {
        setExportMessage("正常にエクスポートされました");
        fetchOrderSummaries(page);
      }
    }

    setLoading(false);
  };

  const handleDownload = async (csvFileName: string) => {
    if (!csvFileName) return;

    setLoading(true);
    const { data, error } = await supabase.storage
      .from('order-csv')
      .download(csvFileName);

    if (error) {
      setExportMessage("CSVのダウンロード失敗: " + error.message);
    } else {
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', csvFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setLoading(false);
  };


  const handleEditMemo = (summary: OrderSummary) => {
    setSelectedSummary(summary);
    setIsModalOpen(true);
  };

  const handleSaveMemo = async (memo: string) => {
    if (selectedSummary) {
      const { error } = await supabase
        .from('order_summary')
        .update({ memo })
        .eq('id', selectedSummary.id);

      if (error) {
        console.error("メモの更新失敗: ", error);
      } else {
        fetchOrderSummaries(page);
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Container>
      <h2>受注CSV管理</h2>
      <Box display="flex" justifyContent="flex-start" mb={2}>
        <Button variant="contained" color="secondary" onClick={handleCSVExport}>
          CSVエクスポート
        </Button>
        <Box display={"flex"} alignItems={"center"} ml={2}>
        ※未集計の受注CSVデータを生成する
        </Box>
      </Box>
      {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}
      {exportMessage && (
        <Alert severity={exportMessage.includes("正常") ? "success" : "error"} sx={{ mb: 2 }}>
          {exportMessage}
        </Alert>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>集計範囲</TableCell>
            <TableCell>集計期間</TableCell>
            <TableCell>メモ</TableCell>
            <TableCell>ダウンロード</TableCell>
            <TableCell>ダウンロード時刻</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderSummaries.map((summary, index) => (
            <TableRow key={index}>
              <TableCell>{summary.id}</TableCell>
              <TableCell>{summary.start_order.受注番号}〜{summary.last_order.受注番号}</TableCell>
              <TableCell>
                {index < orderSummaries.length - 1 ? formatDate(orderSummaries[index + 1].download_date) : ''}〜
                <br />
                {formatDate(summary.download_date)}
              </TableCell>
              <TableCell>
                {summary.memo &&
                  <Box style={{ maxWidth: '250px', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                    {summary.memo}
                    <br />
                  </Box>
                }
                <Button variant="outlined" color="primary" onClick={() => handleEditMemo(summary)}>
                  メモを編集
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleDownload(summary.csv_file_name)}
                  disabled={!summary.csv_file_name}
                >
                  ダウンロード
                </Button>
              </TableCell>
              <TableCell>
                {formatDate(summary.download_date)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination count={totalPages} page={page} onChange={handlePageChange} />
      </Box>
      {selectedSummary && (
        <EditMemoModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveMemo}
          initialMemo={selectedSummary.memo}
        />
      )}
    </Container>
  );
}
